import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { CreateUserInputDto } from 'modules/teams/domain/dtos/create-user-input.dto';
import { UpsertChampionshipApplicationDto } from 'modules/teams/domain/dtos/upsert-championship-application.dto';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { addChampionshipTeam } from 'modules/championship/store/championship/actions';
import { RootState } from 'store/store';
import { TeamResponse, UserResponse } from 'modules/teams/domain/dtos/upsert-championship-application-response.dto';
import { UpsertChampionshipApplicationUserDto } from 'modules/teams/domain/dtos/upsert-championship-application-user.dto';

import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { AnnouncedTeamsMap } from 'modules/teams/domain/interfaces/announced-teams-map';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import { Team } from 'modules/teams/domain/interfaces/team';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import { ApiError, toApiError } from 'shared/errors';

const PREFIX = 'teams/championshipTeams';

export const getChampionshipAnnouncedTeams = createAsyncThunk<AnnouncedTeamsMap, { championshipId: number }>(
  `${PREFIX}/getChampionshipAnnouncedTeams`,
  async (payload, { getState }) => {
    try {
      return await teamsRepository.getAnnouncedChampionshipTeams(payload.championshipId);
    } catch (err) {
      console.error(`${PREFIX}/getChampionshipAnnouncedTeams error:`, err);
      throw err;
    }
  },
);

/**
 * Флоу заявок v2: команды соревнования + заявки одним запросом.
 * Отдельный экшен, а не замена старого: v1-экран не должен внезапно начать показывать
 * команды, у которых нет ни одной заявки. Пишет в ту же ветку `state.data`.
 */
export const getChampionshipTeamsV2 = createAsyncThunk<AnnouncedTeamsMap, { championshipId: number }>(
  `${PREFIX}/getChampionshipTeamsV2`,
  async (payload) => {
    try {
      return await teamsRepository.getChampionshipTeamsWithApplications(payload.championshipId);
    } catch (err) {
      console.error(`${PREFIX}/getChampionshipTeamsV2 error:`, err);
      throw err;
    }
  },
);

export const upsertChampionshipPlayerApplication = createAsyncThunk<
  PlayerApplication,
  UpsertChampionshipApplicationUserDto,
  { rejectValue: ApiError }
>(`${PREFIX}/upsertChampionshipPlayerApplication`, async (payload, { dispatch, rejectWithValue }) => {
  try {
    const result = await teamsRepository.upsertChampionshipApplicationForUser(payload);
    await dispatch(getChampionshipAnnouncedTeams({ championshipId: payload.championshipId }));

    return result;
  } catch (error) {
    console.error(`${PREFIX}/upsertChampionshipPlayerApplication error:`, error);

    return rejectWithValue(toApiError(error));
  }
});

export const championshipPlayerApplicationChangeNumber = createAsyncThunk<
  PlayerApplication,
  Omit<UpsertChampionshipApplicationUserDto, 'id'> & { id: number }
>(`${PREFIX}/championshipPlayerApplicationChangeNumber`, async (payload, { dispatch, rejectWithValue }) => {
  try {
    const result = await teamsRepository.upsertChampionshipApplicationForUser(payload);

    dispatch(
      playerChampionshipApplicationNumberChanged({
        id: payload.id,
        teamId: payload.teamId,
        number: payload.createUserOrUpdateApplication?.number,
        championshipId: payload.championshipId,
      }),
    );

    return result;
  } catch (error) {
    console.error(`${PREFIX}/championshipPlayerApplicationChangeNumber error:`, error);

    return rejectWithValue(toApiError(error));
  }
});

export const championshipPlayerApplicationChangePosition = createAsyncThunk<
  PlayerApplication,
  Omit<UpsertChampionshipApplicationUserDto, 'id'> & { id: number }
>(`${PREFIX}/championshipPlayerApplicationChangePosition`, async (payload, { dispatch, rejectWithValue }) => {
  try {
    const result = await teamsRepository.upsertChampionshipApplicationForUser(payload);

    dispatch(
      playerChampionshipApplicationPositionChanged({
        id: payload.id,
        teamId: payload.teamId,
        position: payload.createUserOrUpdateApplication?.position as PlayerPosition,
        championshipId: payload.championshipId,
      }),
    );

    return result;
  } catch (error) {
    console.error(`${PREFIX}/championshipPlayerApplicationChangeNumber error:`, error);

    return rejectWithValue(toApiError(error));
  }
});

export const upsertChampionshipTeamApplication = createAsyncThunk<
  { teamId: number; status: AnnouncedTeamStatus; applicationStatus: ApplicationStatus } | null,
  { teamId: number; status: AnnouncedTeamStatus; championshipId: number },
  { rejectValue: ApiError }
>(`${PREFIX}/upsertChampionshipTeamApplication`, async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState() as RootState;
    const { data } = state.teams.championshipTeams;
    if (!data) {
      return null;
    }

    let applicationStatus: ApplicationStatus;
    switch (payload.status) {
      case AnnouncedTeamStatus.ACCEPTED:
        applicationStatus = ApplicationStatus.ACCEPTED;
        break;
      case AnnouncedTeamStatus.DECLINED:
        applicationStatus = ApplicationStatus.DECLINED;
        break;
      default:
        applicationStatus = ApplicationStatus.PENDING;
    }

    const apps: PlayerApplication[] = data.byId[payload.championshipId][payload.teamId].applications;
    const createUserOrUpdateApplications: CreateUserInputDto[] = apps.map((app) => ({
      userId: app.player.id,
      name: app.player.firstName,
      surname: app.player.lastName,
      middleName: app.player.middleName,
      birthDate: app.player.birthDate,
      weight: app.player.weight,
      height: app.player.height,
      number: app.number,
      position: app.position ? app.position.toUpperCase() : app.position,
      state: applicationStatus.toUpperCase() as ApplicationStatus,
    }));

    const applicationPayload: UpsertChampionshipApplicationDto = {
      teamId: payload.teamId,
      championshipId: apps[0].championshipId,
      applications: createUserOrUpdateApplications,
    };

    await teamsRepository.upsertChampionshipApplication(applicationPayload);

    return {
      ...payload,
      applicationStatus,
    };
  } catch (err) {
    console.error(`${PREFIX}/upsertChampionshipTeamApplication error:`, err);
    // Не `throw`: сериализованная RTK ошибка теряет код, а по нему экран узнаёт отказ 7016.
    return rejectWithValue(toApiError(err));
  }
});

// export const announceNewTeam = createAction<Team>(`${PREFIX}/announceNewTeam`);
export const announceNewTeam = createAsyncThunk<
  { team: Team; championshipId: number },
  { team: Team; championshipId: number },
  { state: RootState }
>(`${PREFIX}/announceNewTeam`, async (payload, { dispatch, getState }) => {
  try {
    const state = getState() as RootState;
    const championship = state.championships.championship.data!.byId[payload.championshipId];
    // `unwrap` обязателен: без него отказ сохранения не доходит до вызывающей стороны,
    // и команда рисуется в списке заявок, хотя на бэкенде её нет.
    await dispatch(addChampionshipTeam({ teamId: payload.team.id, championshipId: payload.championshipId })).unwrap();
    return { team: payload.team, championshipId: championship.id };
  } catch (err) {
    console.error(`${PREFIX}/announceNewTeam error:`, err);
    throw err;
  }
});

export const playerChampionshipApplicationNumberChanged = createAction<{
  teamId: number;
  id: number;
  number?: number;
  championshipId: number;
}>(`${PREFIX}/playerChampionshipApplicationNumberChanged`);

export const playerChampionshipApplicationPositionChanged = createAction<{
  teamId: number;
  id: number;
  position?: PlayerPosition;
  championshipId: number;
}>(`${PREFIX}/playerChampionshipApplicationPositionChanged`);

export const upsertChampionshipApplicationUsers = createAsyncThunk<
  { team: Omit<TeamResponse, 'applicationsByChampionship'>; applications: Pick<UserResponse, 'id' | 'state'>[] } | null,
  UpsertChampionshipApplicationDto,
  { rejectValue: ApiError }
>(`${PREFIX}/upsertChampionshipApplicationUsers`, async (payload, { dispatch, getState, rejectWithValue }) => {
  try {
    const state = getState() as RootState;
    const { data } = state.teams.championshipTeams;
    if (!data) {
      return null;
    }

    const applicationPayload: UpsertChampionshipApplicationDto = {
      teamId: payload.teamId,
      championshipId: payload.championshipId,
      applications: payload.applications,
    };

    const { team, users } = await teamsRepository.upsertChampionshipApplication(applicationPayload);

    // Массово принять можно и игрока, у которого заявки ещё не было — в список он попал из состава команды.
    // Редьюсер такую запись не создаёт, поэтому перечитываем заявки целиком, иначе игрок останется «Не заявленным».
    await dispatch(getChampionshipAnnouncedTeams({ championshipId: payload.championshipId }));

    return {
      team,
      applications: users,
    };
  } catch (error) {
    console.error(`${PREFIX}/upsertChampionshipApplicationUsers error:`, error);

    return rejectWithValue(toApiError(error));
  }
});

/**
 * Правка номера и амплуа игрока прямо в строке состава (заявки v2).
 *
 * Отдельный thunk, а не `championshipPlayerApplicationChangeNumber` / `…ChangePosition`:
 * их редьюсеры ищут заявку по `app.id`, а у игрока, пришедшего из состава команды, `id`
 * фиктивный — `mapPlayerToAnnounced` ставит `new Date().getTime()`, общий на всех игроков
 * одного вызова (`announced-teams.mapper.ts:96`). Здесь запись обновляется по `player.id`.
 *
 * Заявки соревнования после правки **не перечитываются**: на каждый введённый номер это
 * лишний тяжёлый запрос, а всё нужное для строки приходит в ответе мутации.
 */
export const updateChampionshipPlayerApplicationDetails = createAsyncThunk<
  PlayerApplication,
  Omit<UpsertChampionshipApplicationUserDto, 'id'>,
  { rejectValue: ApiError }
>(`${PREFIX}/updateChampionshipPlayerApplicationDetails`, async (payload, { rejectWithValue }) => {
  try {
    const result = await teamsRepository.upsertChampionshipApplicationForUser(payload);

    // Бэкенд отдаёт амплуа заглавными (`FORWARD`), доменный enum — строчный.
    // Без приведения селект в строке не находит выбранный вариант.
    return { ...result, position: result.position?.toLowerCase() as PlayerPosition | undefined };
  } catch (error) {
    console.error(`${PREFIX}/updateChampionshipPlayerApplicationDetails error:`, error);

    return rejectWithValue(toApiError(error));
  }
});
